import {Alert, Anchor, Badge, Breadcrumbs, Card, Group, Paper, SimpleGrid, Stack, Text, TextInput, Title} from "@mantine/core";
import {DataTable, type DataTableSortStatus} from "mantine-datatable";
import {useMemo, useState} from "react";
import {Link, useParams} from "react-router";
import Page from "../../components/Page/Page.tsx";
import LoaderScreen from "../../components/Loader/LoaderScreen.tsx";
import useDataTableSessionState from "../../hooks/useDataTableSessionState.ts";
import useVendorsData from "./useVendorsData";
import {getWareVendorRows, getWareCategoryColor, getWareCategoryLabel, normalizeWareCategory} from "./utils";
import type {WareVendorRow} from "./types";
import {useSWCLocation} from "../../utils/api.ts";
import WareVendorMap from "./WareVendorMap";
import WareImage from "./WareImage.tsx";

const IMG_SIZE = 80;

const integerFormat = new Intl.NumberFormat();
const priceFormat = new Intl.NumberFormat(undefined, {maximumFractionDigits: 2});

const sortRows = (rows: WareVendorRow[], sortStatus: DataTableSortStatus<WareVendorRow>): WareVendorRow[] => {
  const accessor = sortStatus.columnAccessor as keyof WareVendorRow;
  const sorted = [...rows].sort((a, b) => {
    const left = a[accessor];
    const right = b[accessor];

    if (typeof left === 'number' || typeof right === 'number') {
      return (typeof left === 'number' ? left : -Infinity) - (typeof right === 'number' ? right : -Infinity);
    }

    return String(left ?? '').localeCompare(String(right ?? ''));
  });

  return sortStatus.direction === 'desc' ? sorted.reverse() : sorted;
};

const renderImageCell = (row: WareVendorRow) => {
  return <WareImage src={row.images.small} alt={row.entityName || ''} size={IMG_SIZE} category={row.category}/>;
}

const renderVendorName =  (row: WareVendorRow) => {
  return <Link to={`/vendors/${row.vendorId}`}>{row.vendorName}</Link>
}

export default function WareVendors() {
  const {type, wareKey} = useParams<{ type?: string; wareKey?: string }>();
  const {vendors, wareRows, isPending, isError, error} = useVendorsData();
  const {location} = useSWCLocation();
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null);
  const {sortStatus, setSortStatus, filters, setFilter} = useDataTableSessionState<
    DataTableSortStatus<WareVendorRow>,
    { search: string }
  >({
    storageKey: `vendors:ware-vendors:${type ?? 'unknown'}:${wareKey ?? 'unknown'}`,
    initialSortStatus: {
      columnAccessor: 'distance',
      direction: 'asc',
    },
    initialFilters: {
      search: '',
    },
  });
  const search = typeof filters.search === 'string' ? filters.search : '';


  const category = normalizeWareCategory(type ?? '');

  const ware = useMemo(() => {
    if (!wareKey) {
      return null;
    }
    return wareRows.find(row => row.category === category && row.wareKey === wareKey) ?? null;
  }, [wareRows, category, wareKey]);

  const vendorRows = useMemo(() => {
    if (!ware) {
      return [];
    }
    return getWareVendorRows(vendors, ware, location);
  }, [vendors, ware, location]);

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) {
      return vendorRows;
    }
    return vendorRows.filter((row) =>
      row.vendorName.toLowerCase().includes(needle) ||
      row.ownerName.toLowerCase().includes(needle) ||
      row.location.container?.name.toLowerCase().includes(needle) ||
      row.location.system?.name.toLowerCase().includes(needle) ||
      row.location.sector?.name.toLowerCase().includes(needle)
    );
  }, [vendorRows, search]);

  const sortedRows = useMemo(() => sortRows(filteredRows, sortStatus), [filteredRows, sortStatus]);

  if (isPending) {
    return <LoaderScreen message="Loading ware vendors"/>;
  }

  return (
    <Page>
      <Breadcrumbs>
        <Anchor component={Link} to="/wares">Wares</Anchor>
        {ware && <Anchor component={Link} to={`/wares/${type}/${wareKey}`}>{ware.name}</Anchor>}
      </Breadcrumbs>
      <title>{ware ? `${ware.name} Vendors` : 'Ware Vendors'}</title>

      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div style={{flex: 1, minWidth: 280}}>
            <Title order={1}>{ware?.name ?? 'Ware not found'}</Title>
            {ware ? (
              <Stack gap="md">
                <Group gap={8} mt="xs">
                  <Badge color={getWareCategoryColor(ware.category)} variant="light">
                    {getWareCategoryLabel(ware.category)}
                  </Badge>
                  <Badge color="blue" variant="light">{integerFormat.format(ware.vendorCount)} vendors</Badge>
                </Group>
                <SimpleGrid cols={3} spacing="sm">
                  <Card withBorder radius="md" padding="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Min Price</Text>
                    <Text size="xl" fw={700} c="green">
                      {ware.minPrice === null ? '-' : priceFormat.format(ware.minPrice)}
                    </Text>
                  </Card>
                  <Card withBorder radius="md" padding="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Avg Price</Text>
                    <Text size="xl" fw={700} c="yellow">
                      {ware.avgPrice === null ? '-' : priceFormat.format(ware.avgPrice)}
                    </Text>
                  </Card>
                  <Card withBorder radius="md" padding="md">
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Max Price</Text>
                    <Text size="xl" fw={700} c="red">
                      {ware.maxPrice === null ? '-' : priceFormat.format(ware.maxPrice)}
                    </Text>
                  </Card>
                </SimpleGrid>
              </Stack>
            ) : null}

          </div>

          <WareVendorMap rows={sortedRows} hoveredRowId={hoveredRowId} userLocation={location}/>
        </Group>

        {isError ? (
          <Alert color="red" title="Unable to load ware vendors">
            {error instanceof Error ? error.message : 'Request failed'}
          </Alert>
        ) : null}

        {!ware ? (
          <Alert color="yellow" title="Ware not found">
            No ware matched the selected route.
          </Alert>
        ) : (
          <Paper withBorder p="md" radius="md">
            <Group justify="space-between" mb="md" align="end">
              <TextInput
                label="Search vendors"
                placeholder="Vendor name, owner or location"
                value={search}
                onChange={(e) => setFilter('search', e.currentTarget.value)}
                style={{flex: 1, minWidth: 220}}
              />
              <Text c="dimmed" size="sm">
                {integerFormat.format(sortedRows.length)} listing{sortedRows.length === 1 ? '' : 's'}
              </Text>
            </Group>

            <DataTable<WareVendorRow>
              idAccessor="id"
              records={sortedRows}
              customRowAttributes={(record) => ({
                onMouseEnter: () => setHoveredRowId(record.id),
                onMouseLeave: () => setHoveredRowId((current) => (current === record.id ? null : current)),
              })}
              sortStatus={sortStatus}
              onSortStatusChange={setSortStatus}
              columns={[
                {accessor: 'images', title: 'Image', render: renderImageCell},
                {accessor: 'name', title: 'Name', sortable: true},
                {accessor: 'vendorName', title: 'Vendor', sortable: true, render: renderVendorName},
                {accessor: 'ownerName', title: 'Owner', sortable: true},
                {
                  accessor: 'price',
                  title: 'Price',
                  sortable: true,
                  render: (row) => priceFormat.format(row.price),
                },
                {
                  accessor: 'quantity',
                  title: 'Qty',
                  sortable: true,
                  render: (row) => integerFormat.format(row.quantity),
                },
                {
                  accessor: 'location.container.name',
                  title: 'Location'
                },
                {
                  accessor: 'location.system.name',
                  title: 'System',
                  sortable: true,
                  render: row => {
                    return <Stack gap={2}>
                      <Text size="sm" c="dimmed">{row.location.sector?.name}</Text>
                      <Text
                        size="sm">{row.location?.system?.name.startsWith('Deep Space') ? row.location?.system?.name : `${row.location?.system?.name} (${row.location?.coordinates?.galaxy?.x}, ${row.location?.coordinates?.galaxy?.y})`}</Text>
                    </Stack>
                  }
                },
                {
                  accessor: 'distance',
                  title: 'Distance',
                  hidden: location === null,
                  sortable: true,
                }
              ]}
            />
          </Paper>
        )}
      </Stack>
    </Page>
  );
}
